import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { replace } from 'react-router-redux';
import { withTranslation } from 'react-i18next';
import {
  Button, Container, Dimmer, Dropdown, Form, Grid, Icon, Input, Label, Loader, Message, Modal,
  Search, Segment, Table,
} from 'semantic-ui-react';

import { fetchM } from '../../actions/fetchM';
import '../../styles/components/crmOps.less';
import NavBar from '../../components/NavBar';
import PageNav from '../../components/PageNav';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import { decodeSearch, encodeSearch } from '../../utils/url';
import { newRequestGuard } from '../../utils/crmLink';
import { providerLabel } from '../../utils/crmContext';
import {
  canResolve, eventLabel, formatWhen, isParked, lastAnswer, statusChip,
} from '../../utils/crmSync';
import {
  LIST_LIMIT, LIST_PATH, REASONS, STATUS_OPTIONS,
  actionPath, bookingEventsPath, bookingLabel, bookingPath, canSeeCrmOps, listParamsFrom, listPath,
  pageOf, projectFilterOf, projectFilterValue, providerOptions, queryWith, reasonLabel, statusOf,
  unitAndProject,
} from '../../utils/crmOps';

// The CRM ops list (MS-6630, T6.5): every outbox row that needs a person, across bookings, with
// Retry and Mark resolved inline. This is the fallback the 31 Aug email said we would rather not
// ship; the contract forces it (a duplicate 1.0 answers 409 and there is no GET to check), so it
// ships visibly, at /integrations/crm/, for admins of an OutPerform tenant (the route is gated by
// hasOutperformTenant, MS-6631).
//
// The filters are the URL (decodeSearch / encodeSearch), so a list can be bookmarked or sent to a
// colleague; utils/crmOps turns them into the server's query and is where the rules are tested.
// The rows are the redacted view (MS-6646): the reason, the instruction, codes, the masked subject
// and the booking's code / unit / project, never a request, a response or the provider's message.
//
// Retry is offered on every parked row. Unlike the per-booking panel, this list cannot see the
// booking's other rows, so it cannot know whether a row is the oldest parked of its chain; the
// server does (MS-6615) and answers 409 with the reason, which is shown beside the row and the
// list re-read. That is the honest shape for a cross-booking list: offer, and let the one place
// that knows decide.
//
// The CSRF token for an action is read from the booking's own events route just before the POST:
// gorilla/csrf scopes its cookie to the path that issued it, so the cross-booking list's token
// would arrive without its cookie at /bookings/:id/crm/events/:event/retry/ and be refused.
const ns = { ns: TRANSLATION_FILES.translation };

export class CrmSyncList extends Component {
  static propTypes = {
    location: PropTypes.shape({
      search: PropTypes.string,
      query: PropTypes.object,
    }),
    appConfig: PropTypes.shape({ data: PropTypes.object }),
    auth: PropTypes.shape({
      isSuperAdmin: PropTypes.bool,
      isAdmin: PropTypes.bool,
      isAdminBasic: PropTypes.bool,
    }),
    dispatch: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
  }

  static defaultProps = {
    location: { search: '', query: {} },
    appConfig: { data: {} },
    auth: {},
  }

  static mapStateToProps = state => ({
    appConfig: state.appConfig,
    auth: state.auth,
  })

  state = {
    rows: null,
    count: 0,
    loading: false,
    error: '',
    actionError: '',
    resolving: null,
    note: '',
    acting: null,
    projectSearch: '',
    projectResults: [],
    projectLoading: false,
  };

  componentDidMount() {
    this.mounted = true;
    this.load();
  }

  componentDidUpdate(prev) {
    const before = prev.location && prev.location.search;
    const now = this.props.location && this.props.location.search;
    // A new filter is a new list: nothing of the previous one — rows, count, refusal, modal — may
    // stay on screen, least of all actionable rows should the new read fail.
    if (before !== now) this.load({ fresh: true });
  }

  componentWillUnmount() {
    this.mounted = false;
    this.guard.invalidate();
    clearTimeout(this.searchTimer);
  }

  // Filters live in the URL; changing one navigates (replace, not push: a filter is not a page a
  // back button should revisit) and componentDidUpdate reloads.
  setQuery = (changes, { resetPage = true } = {}) => {
    const next = queryWith(this.query(), changes, { keepPage: !resetPage });
    this.props.dispatch(replace(`${LIST_PATH}?${encodeSearch(next)}`));
  }

  query = () => decodeSearch((this.props.location && this.props.location.search) || '');

  canAct = () => canSeeCrmOps(this.props.auth)

  load = async ({ fresh = false } = {}) => {
    if (!this.mounted) return;
    const token = this.guard.begin();
    const params = listParamsFrom(this.query());
    this.setState(fresh ? {
      loading: true, rows: null, count: 0, error: '', actionError: '', resolving: null, note: '',
    } : { loading: true });
    let res;
    try {
      // returnForbidden / ignoreNotFound: fetchM otherwise navigates the page away; a 403 here
      // is "not your list", said on the page.
      res = await this.props.dispatch(fetchM(listPath(params), {
        credentials: 'include', returnForbidden: true, ignoreNotFound: true,
      }));
    } catch (err) {
      res = err && err.res;
    }
    if (!this.mounted || !this.guard.isCurrent(token)) return;
    if (res && res.status === 403) {
      // Not this user's list: nothing to show, nothing to act on, no modal left open over it.
      this.setState({
        rows: [],
        count: 0,
        loading: false,
        error: this.props.t('You do not have access to the CRM sync list.', ns),
        actionError: '',
        resolving: null,
        note: '',
      });
      return;
    }
    let rows = null;
    if (res && res.ok) {
      try {
        rows = await res.json();
      } catch (e) {
        rows = null;
      }
    }
    if (!this.mounted || !this.guard.isCurrent(token)) return;
    if (!Array.isArray(rows)) {
      let text = '';
      try {
        text = res && !res.ok ? await res.text() : '';
      } catch (e) {
        text = '';
      }
      if (!this.mounted || !this.guard.isCurrent(token)) return;
      this.setState({ loading: false, error: text || this.props.t('The CRM sync list could not be loaded.', ns) });
      return;
    }
    let count = rows.length;
    try {
      const c = parseInt(res.headers.get('m-data-count'), 10);
      if (Number.isFinite(c)) count = c;
    } catch (e) {
      // the header is a convenience: the rows are the answer
    }
    // Filtered onto a page past the end (a row was resolved elsewhere): back to the first page.
    if (rows.length === 0 && count > 0 && pageOf(this.query()) > 1) {
      this.setQuery({}, { resetPage: true });
      return;
    }
    const { resolving } = this.state;
    const stillParked = resolving && rows.some(r => r.id === resolving.id && canResolve(r));
    this.setState({
      rows,
      count,
      loading: false,
      error: '',
      resolving: stillParked ? resolving : null,
      note: stillParked ? this.state.note : '',
    });
  }

  // Only the newest read may act (newRequestGuard): a filter changed while a page was loading
  // must not have the old page land on top of the new one.
  guard = newRequestGuard();

  searchSeq = 0;

  handleStatus = (e, { value }) => this.setQuery({ status: value });
  handleProvider = (e, { value }) => this.setQuery({ provider: value });
  handleReason = (e, { value }) => this.setQuery({ reason: value });
  handleDateFrom = e => this.setQuery({ dateFrom: e.target.value });
  handleDateTo = e => this.setQuery({ dateTo: e.target.value });
  handlePage = page => this.setQuery({ page: String(page) }, { resetPage: false });
  handleClearProject = () => {
    this.resetProjectSearch();
    this.setQuery({ projectFilter: '' });
  }
  handleClearAll = () => {
    this.resetProjectSearch();
    this.props.dispatch(replace(LIST_PATH));
  }

  // resetProjectSearch retires whatever project search is debouncing or in flight (searchSeq) and
  // empties the box, so a late answer neither shows for a different text nor leaves the spinner on.
  resetProjectSearch = () => {
    clearTimeout(this.searchTimer);
    this.searchSeq += 1;
    this.setState({ projectSearch: '', projectResults: [], projectLoading: false });
  }

  // Every change of the search box retires the previous search the same way.
  handleProjectSearch = (e, { value }) => {
    clearTimeout(this.searchTimer);
    this.searchSeq += 1;
    this.setState({ projectSearch: value, projectResults: [], projectLoading: false });
    if (!value || !value.trim()) return;
    this.searchTimer = setTimeout(() => this.searchProjects(value.trim()), 400);
  }

  searchProjects = async (value) => {
    this.searchSeq += 1;
    const seq = this.searchSeq;
    this.setState({ projectLoading: true });
    let results = [];
    try {
      const res = await this.props.dispatch(fetchM(`/search/projects/?limit=5&search=${encodeURIComponent(value)}`, {
        credentials: 'include', returnForbidden: true, ignoreNotFound: true,
      }));
      const json = res && res.ok ? await res.json() : [];
      results = (json || []).map(p => ({
        key: p.id, id: p.id, title: p.shortName || p.name || p.id, description: p.state || '',
      }));
    } catch (err) {
      results = [];
    }
    if (!this.mounted || seq !== this.searchSeq) return;
    this.setState({ projectResults: results, projectLoading: false });
  }

  handleProjectSelect = (e, { result }) => {
    this.resetProjectSearch();
    this.setQuery({ projectFilter: projectFilterValue({ id: result.id, name: result.title }) });
  }

  // tokenFor reads a CSRF token issued on the booking's own events path (see the file comment).
  tokenFor = async (row) => {
    let res;
    try {
      res = await this.props.dispatch(fetchM(bookingEventsPath(row), {
        credentials: 'include', returnForbidden: true, ignoreNotFound: true,
      }));
    } catch (err) {
      res = err && err.res;
    }
    if (!res || !res.ok) return '';
    try {
      return res.headers.get('X-CSRF-Token') || '';
    } catch (e) {
      return '';
    }
  }

  // Actions: the same contract as the per-booking panel (MS-6629), against the row's own booking.
  act = async (row, action, note) => {
    const { t } = this.props;
    const listAtPost = (this.props.location && this.props.location.search) || '';
    this.setState({ acting: row.id, actionError: '' });
    const token = await this.tokenFor(row);
    if (!this.mounted) return;
    let res = null;
    if (token) {
      try {
        res = await this.props.dispatch(fetchM(actionPath(row, action), {
          credentials: 'include',
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Csrf-Token': token },
          body: JSON.stringify({ note: note || '' }),
          returnForbidden: true,
          ignoreNotFound: true,
        }));
      } catch (err) {
        res = err && err.res;
      }
    }
    if (!this.mounted) return;
    let error = '';
    if (!res || !res.ok) {
      let text = '';
      try {
        text = res ? await res.text() : '';
      } catch (e) {
        text = '';
      }
      if (!this.mounted) return;
      error = text || t('The action could not be completed.', ns);
    }
    // The filters moved on while the POST was out: the row's booking got the action (nothing wrong
    // was sent), but its refusal is about a list no longer on screen, and componentDidUpdate has
    // already started the read for the new one, which this must not supersede.
    const listNow = (this.props.location && this.props.location.search) || '';
    if (listNow !== listAtPost) {
      this.setState({ acting: null, resolving: null, note: '' });
      return;
    }
    // Re-read whether it worked or not: on a 409 the fresh list is the explanation, shown next to
    // the refusal, which the re-read must not wipe (actionError is separate from error).
    this.setState({ acting: null, resolving: null, note: '', actionError: error });
    await this.load();
  }

  // Judged against the rows on the screen now, as the panel does: a row that a re-read has moved
  // gets no POST the server would refuse.
  actFor = (row, action, note) => {
    const current = (this.state.rows || []).find(r => r.id === row.id);
    if (!current || !isParked(current)) return Promise.resolve();
    if (action === 'retry' && (current.event === 'contact_details_changed' && current.needsHumanReason === 'contact_ref_missing')) return Promise.resolve();
    return this.act(current, action, note);
  }

  handleRetry = row => this.actFor(row, 'retry', '');
  handleOpenResolve = row => this.setState({ resolving: row, note: '', actionError: '' });
  handleCloseResolve = () => this.setState({ resolving: null, note: '' });
  handleNote = (e, { value }) => this.setState({ note: value });
  handleResolve = () => {
    const { resolving, note } = this.state;
    if (!resolving || !note.trim()) return Promise.resolve();
    return this.actFor(resolving, 'resolve', note.trim());
  }

  handleRefresh = () => {
    this.setState({ actionError: '' });
    this.load();
  }

  renderRow(row) {
    const { t } = this.props;
    const { acting, loading } = this.state;
    const chip = statusChip(row);
    const busy = acting !== null || loading;
    const parked = isParked(row);
    const subject = row.subject || {};
    const unit = unitAndProject(row);
    // lastAnswer's strings live in the translation namespace, as the panel's do
    const answer = lastAnswer(row, (k, o) => t(k, { ...ns, ...o }));
    return (
      <Table.Row key={row.id} data-testid={`crm-ops-row-${row.id}`}>
        <Table.Cell>{formatWhen(row.createdAt)}</Table.Cell>
        <Table.Cell>
          <Link to={bookingPath(row)}>{bookingLabel(row)}</Link>
          {unit ? <div className="crm-ops__muted">{unit}</div> : null}
          {subject.name ? <div className="crm-ops__muted">{subject.name}</div> : null}
        </Table.Cell>
        <Table.Cell>{providerLabel(row.provider)}</Table.Cell>
        <Table.Cell>{t(eventLabel(row.event), ns)}</Table.Cell>
        <Table.Cell>
          <Label size="small" color={chip.color} data-testid={`crm-ops-status-${row.id}`}>{t(chip.label, ns)}</Label>
        </Table.Cell>
        <Table.Cell textAlign="center">{row.attempts || 0}</Table.Cell>
        <Table.Cell>
          {/* The reason stays on a row as history; the instruction is advice, and only while parked
              (a resolved row's advice has been acted on, and the server withholds it too). */}
          {row.needsHumanReason ? (
            <div>
              <b>{t(reasonLabel(row.needsHumanReason), ns)}</b> <code>{row.needsHumanReason}</code>
              {parked && row.instruction ? <div className="crm-ops__instruction">{row.instruction}</div> : null}
            </div>
          ) : null}
          <div className="crm-ops__muted">{answer}</div>
        </Table.Cell>
        {this.canAct() ? (
          <Table.Cell collapsing>
            {parked ? (
              <span>
                <Button
                  size="mini"
                  compact
                  primary
                  disabled={busy || (row.event === 'contact_details_changed' && row.needsHumanReason === 'contact_ref_missing')}
                  title={(row.event === 'contact_details_changed' && row.needsHumanReason === 'contact_ref_missing') ? row.instruction : undefined}
                  loading={acting === row.id}
                  onClick={() => this.handleRetry(row)}
                  data-testid={`crm-ops-retry-${row.id}`}
                >
                  {t('Retry', ns)}
                </Button>
                <Button
                  size="mini"
                  compact
                  disabled={busy}
                  onClick={() => this.handleOpenResolve(row)}
                  data-testid={`crm-ops-resolve-${row.id}`}
                >
                  {t('Mark resolved', ns)}
                </Button>
              </span>
            ) : null}
          </Table.Cell>
        ) : null}
      </Table.Row>
    );
  }

  render() {
    const { t, appConfig } = this.props;
    const {
      rows, count, loading, error, actionError, resolving, note, acting,
      projectSearch, projectResults, projectLoading,
    } = this.state;
    const query = this.query();
    const project = projectFilterOf(query);
    const problem = actionError || error;
    const statusOptions = STATUS_OPTIONS.map(o => ({ value: o.value, text: t(o.text, ns) }));
    const reasonOptions = [{ value: '', text: t('Any reason', ns) }]
      .concat(REASONS.map(r => ({ value: r.value, text: `${t(r.text, ns)} (${r.value})` })));
    const providers = [{ value: '', text: t('Any CRM', ns) }].concat(providerOptions(appConfig));
    const page = pageOf(query);
    return (
      <Container>
        <NavBar header={t('CRM sync', ns)} />
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <Form size="small">
                <Form.Group widths="equal">
                  <Form.Field>
                    <label htmlFor="crm-ops-status">{t('Status', ns)}</label>
                    <Dropdown id="crm-ops-status" selection fluid options={statusOptions} value={statusOf(query)} onChange={this.handleStatus} data-testid="crm-ops-status" />
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor="crm-ops-provider">{t('CRM', ns)}</label>
                    <Dropdown
                      id="crm-ops-provider"
                      selection
                      fluid
                      options={providers}
                      value={String(query.provider || '')}
                      placeholder={t('Any CRM', ns)}
                      onChange={this.handleProvider}
                      data-testid="crm-ops-provider"
                    />
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor="crm-ops-reason">{t('Reason', ns)}</label>
                    <Dropdown
                      id="crm-ops-reason"
                      selection
                      search
                      fluid
                      options={reasonOptions}
                      value={String(query.reason || '')}
                      placeholder={t('Any reason', ns)}
                      onChange={this.handleReason}
                      data-testid="crm-ops-reason"
                    />
                  </Form.Field>
                </Form.Group>
                <Form.Group widths="equal">
                  <Form.Field>
                    <label htmlFor="crm-ops-project">{t('Project', ns)}</label>
                    {project ? (
                      <span>
                        <Label data-testid="crm-ops-project-chosen">{project.name || project.id}</Label>
                        <Button
                          type="button"
                          size="mini"
                          basic
                          compact
                          icon="delete"
                          onClick={this.handleClearProject}
                          aria-label={t('Clear project', ns)}
                          data-testid="crm-ops-project-clear"
                        />
                      </span>
                    ) : (
                      <Search
                        id="crm-ops-project"
                        fluid
                        loading={projectLoading}
                        value={projectSearch}
                        results={projectResults}
                        onSearchChange={this.handleProjectSearch}
                        onResultSelect={this.handleProjectSelect}
                        placeholder={t('Find project', ns)}
                        noResultsMessage={t('No projects found.', ns)}
                        data-testid="crm-ops-project"
                      />
                    )}
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor="crm-ops-from">{t('Created from', ns)}</label>
                    <Input id="crm-ops-from" type="date" value={String(query.dateFrom || '')} onChange={this.handleDateFrom} data-testid="crm-ops-from" />
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor="crm-ops-to">{t('Created to', ns)}</label>
                    <Input id="crm-ops-to" type="date" value={String(query.dateTo || '')} onChange={this.handleDateTo} data-testid="crm-ops-to" />
                  </Form.Field>
                  <Form.Field>
                    <label htmlFor="crm-ops-refresh">&nbsp;</label>
                    <Button.Group size="small" fluid>
                      <Button id="crm-ops-refresh" icon labelPosition="left" loading={loading} disabled={loading} onClick={this.handleRefresh} data-testid="crm-ops-refresh">
                        <Icon name="refresh" />{t('Refresh', ns)}
                      </Button>
                      <Button basic onClick={this.handleClearAll} data-testid="crm-ops-clear">{t('Clear filters', ns)}</Button>
                    </Button.Group>
                  </Form.Field>
                </Form.Group>
              </Form>
            </Grid.Column>
          </Grid.Row>
          {problem ? (
            <Grid.Row>
              <Grid.Column width={16}>
                <Message size="small" negative data-testid="crm-ops-error">{problem}</Message>
              </Grid.Column>
            </Grid.Row>
          ) : null}
          <Grid.Row>
            <Grid.Column width={16}>
              <PageNav
                currentPage={page}
                count={count}
                limit={LIST_LIMIT}
                offset={(page - 1) * LIST_LIMIT}
                dataSize={(rows || []).length}
                loading={loading}
                onChange={this.handlePage}
              />
              <Dimmer.Dimmable as="div" dimmed={loading}>
                <Dimmer active={loading} inverted><Loader inline="centered">{t('Loading', ns)}</Loader></Dimmer>
                {rows && rows.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <Table compact size="small" unstackable data-testid="crm-ops-table">
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>{t('Created', ns)}</Table.HeaderCell>
                          <Table.HeaderCell>{t('Booking', ns)}</Table.HeaderCell>
                          <Table.HeaderCell>{t('CRM', ns)}</Table.HeaderCell>
                          <Table.HeaderCell>{t('Event', ns)}</Table.HeaderCell>
                          <Table.HeaderCell>{t('Status', ns)}</Table.HeaderCell>
                          <Table.HeaderCell textAlign="center">{t('Attempts', ns)}</Table.HeaderCell>
                          <Table.HeaderCell>{t('Reason and last answer', ns)}</Table.HeaderCell>
                          {this.canAct() ? <Table.HeaderCell /> : null}
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>{rows.map(r => this.renderRow(r))}</Table.Body>
                    </Table>
                  </div>
                ) : null}
                {rows && rows.length === 0 && !problem ? (
                  <Segment textAlign="center" data-testid="crm-ops-empty">{t('Nothing needs attention.', ns)}</Segment>
                ) : null}
              </Dimmer.Dimmable>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Modal open={Boolean(resolving)} size="tiny" onClose={this.handleCloseResolve}>
          <Modal.Header>{t('Mark resolved', ns)}</Modal.Header>
          <Modal.Content>
            {resolving ? (
              <p><b>{bookingLabel(resolving)}</b> · {t(eventLabel(resolving.event), ns)}</p>
            ) : null}
            <p>
              {t('Say what was done in the CRM, or why nothing needs to be. The rows behind this one are delivered next.', ns)}
            </p>
            <Form>
              <Form.TextArea
                value={note}
                onChange={this.handleNote}
                maxLength={2000}
                placeholder={t('Note (required)', ns)}
                aria-label={t('Note (required)', ns)}
                data-testid="crm-ops-note"
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.handleCloseResolve} disabled={acting !== null}>{t('Cancel', ns)}</Button>
            <Button
              primary
              onClick={this.handleResolve}
              disabled={!note.trim() || acting !== null || loading}
              loading={acting !== null}
              data-testid="crm-ops-resolve-confirm"
            >
              {t('Mark resolved', ns)}
            </Button>
          </Modal.Actions>
        </Modal>
      </Container>
    );
  }
}

export default withTranslation(namespaces)(connect(CrmSyncList.mapStateToProps)(CrmSyncList));



// WEBPACK FOOTER //
// ./src/containers/Integrations/CrmSync.jsx